import { FileSearchOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Result,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from "antd";
import debounce from "lodash/debounce";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createInspection,
  fetchInspectionById,
  fetchInventoryDetails,
  fetchStyles,
  isRequestCancelled,
  updateInspection,
} from "apiServices/fabricInspectionService";
import { buildInspectionDetails, buildInspectionPayload, summarizeDefects } from "./buildInspectionPayload";
import { DATE_FORMAT, DISPLAY_DATE_FORMAT, STYLE_PAGE_SIZE, STYLE_SEARCH_DEBOUNCE_MS } from "./constants";
import DefectInputs from "./DefectInputs";
import { getRecordDetails } from "./InspectionReportTable";

const { Text } = Typography;
const { TextArea } = Input;

/** Laravel field → form field that should display the error. */
const SERVER_FIELD_TO_FORM_FIELD = {
  style_id: "style_id",
  sales_contract_id: "style_id",
  buyer_id: "style_id",
  buying_office_id: "style_id",
  sub_contract_factory_id: "style_id",
  supplier_id: "supplier_id",
  item_category_id: "inventory_item_id",
  item_id: "inventory_item_id",
  sub_style_id: "inventory_item_id",
  received_date: "inventory_item_id",
  received_qty: "inventory_item_id",
  received_roll: "inventory_item_id",
  color_id: "color_id",
  inspection_date: "inspection_date",
  inspected_roll: "inspected_roll",
  tag_length: "tag_length",
  tag_actual_length: "tag_actual_length",
  tag_width: "tag_width",
  tag_actual_width: "tag_actual_width",
  note: "note",
};

const positiveNumberRule = (label) => ({
  validator: (_, value) =>
    value === null || value === undefined || value === "" || Number(value) > 0
      ? Promise.resolve()
      : Promise.reject(new Error(`${label} must be greater than 0`)),
});

/**
 * @template {{id:number}} T
 * @param {T[]} base
 * @param {T[]} extra
 * @returns {T[]}
 */
const mergeById = (base, extra) => {
  const map = new Map();
  [...base, ...extra].forEach((item) => {
    if (item?.id != null) map.set(Number(item.id), item);
  });
  return Array.from(map.values());
};

/**
 * @param {import("./types").FabricStyle|null} style
 * @param {import("./types").InventoryItem|null} item
 * @returns {number|null}
 */
const guessColorId = (style, item) => {
  const colorMap = style?.color_map || [];
  if (item?.color_id && colorMap.some((c) => Number(c.color_id) === Number(item.color_id))) {
    return Number(item.color_id);
  }
  const itemName = String(item?.inventory?.item?.name || "").toUpperCase();
  let best = null;
  colorMap.forEach((c) => {
    const colorName = String(c.color_info?.name || "").trim().toUpperCase();
    if (colorName && itemName.includes(colorName) && (!best || colorName.length > best.length)) {
      best = { id: Number(c.color_id), length: colorName.length };
    }
  });
  return best ? best.id : null;
};

/**
 * Minimal style built from a saved record when the style search doesn't return it.
 * @param {import("./types").InspectionRecord} record
 * @returns {import("./types").FabricStyle}
 */
const styleFromRecord = (record) => ({
  id: record.style_id,
  style_no: record.style?.style_no || `Style #${record.style_id}`,
  buyer_id: record.buyer_id,
  buying_office_id: record.buying_office_id,
  sub_contract_factory_id: record.sub_contract_factory_id,
  buyer_info: record.buyer ? { id: record.buyer.id, name: record.buyer.name } : null,
  buying_office_info: null,
  supplier_ids: record.supplier ? [{ id: record.supplier.id, name: record.supplier.name }] : [],
  active_sales_contract_map_info: { sales_contract_id: record.sales_contract_id },
  color_map: record.color
    ? [{ id: 0, style_id: record.style_id, color_id: record.color.id, color_info: record.color }]
    : [],
});

/**
 * Keeps the saved sales contract, supplier and colour selectable in edit mode.
 * @param {import("./types").FabricStyle} style
 * @param {import("./types").InspectionRecord} record
 * @returns {import("./types").FabricStyle}
 */
const withRecordFallbacks = (style, record) => {
  const suppliers = style.supplier_ids || [];
  const colorMap = style.color_map || [];
  return {
    ...style,
    active_sales_contract_map_info: { ...(style.active_sales_contract_map_info || {}), sales_contract_id: record.sales_contract_id },
    supplier_ids:
      record.supplier && !suppliers.some((s) => Number(s.id) === Number(record.supplier.id))
        ? [...suppliers, { id: record.supplier.id, name: record.supplier.name }]
        : suppliers,
    color_map:
      record.color && !colorMap.some((c) => Number(c.color_id) === Number(record.color.id))
        ? [...colorMap, { id: 0, style_id: record.style_id, color_id: record.color.id, color_info: record.color }]
        : colorMap,
  };
};

const formatDate = (value) => (value ? moment(value).format(DISPLAY_DATE_FORMAT) : "");

/**
 * Mounted fresh for every open (the parent controls mounting), so state never leaks between records.
 * @param {Object} props
 * @param {number|null} props.editId
 * @param {import("./types").DefectCategory[]} props.defectCategories
 * @param {boolean} props.defectCategoriesLoading
 * @param {() => void} props.onCancel
 * @param {(message: string) => void} props.onSaved
 */
const FabricInspectionModal = ({ editId, defectCategories, defectCategoriesLoading, onCancel, onSaved }) => {
  const [form] = Form.useForm();
  const mode = editId ? "edit" : "create";

  const [styleOptions, setStyleOptions] = useState(/** @type {import("./types").FabricStyle[]} */([]));
  const [styleSearch, setStyleSearch] = useState("");
  const [stylePaging, setStylePaging] = useState({ current: 0, last: 1 });
  const [styleLoading, setStyleLoading] = useState(false);
  const styleRequestRef = useRef(0);
  const [selectedStyle, setSelectedStyle] = useState(/** @type {import("./types").FabricStyle|null} */(null));

  const [inventoryItems, setInventoryItems] = useState(/** @type {import("./types").InventoryItem[]} */([]));
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState(/** @type {string|null} */(null));
  const [inventoryReloadKey, setInventoryReloadKey] = useState(0);

  const [record, setRecord] = useState(/** @type {import("./types").InspectionRecord|null} */(null));
  const [recordLoading, setRecordLoading] = useState(mode === "edit");
  const [recordError, setRecordError] = useState(/** @type {string|null} */(null));
  const [recordReloadKey, setRecordReloadKey] = useState(0);
  const pendingItemMatchRef = useRef(/** @type {{item_id:number|null, sub_style_id:number|null}|null} */(null));

  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [generalErrors, setGeneralErrors] = useState(/** @type {string[]} */([]));

  const supplierId = Form.useWatch("supplier_id", form);
  const inventoryItemId = Form.useWatch("inventory_item_id", form);
  const inspectionDate = Form.useWatch("inspection_date", form);
  const inspectedRoll = Form.useWatch("inspected_roll", form);
  const tagActualLength = Form.useWatch("tag_actual_length", form);
  const tagActualWidth = Form.useWatch("tag_actual_width", form);
  const defects = Form.useWatch("defects", form);

  /* --------------------------------- Styles --------------------------------- */

  const loadStyles = useCallback(async (search, page) => {
    const requestId = ++styleRequestRef.current;
    setStyleLoading(true);
    try {
      const result = await fetchStyles({ search, page, perPage: STYLE_PAGE_SIZE });
      if (requestId !== styleRequestRef.current) return;
      setStyleOptions((prev) => (page === 1 ? result.items : mergeById(prev, result.items)));
      setStylePaging({ current: result.currentPage, last: result.lastPage });
    } catch (error) {
      if (requestId === styleRequestRef.current) message.error(error?.message || "Failed to load styles.");
    } finally {
      if (requestId === styleRequestRef.current) setStyleLoading(false);
    }
  }, []);

  const debouncedStyleSearch = useMemo(
    () =>
      debounce((value) => {
        setStyleSearch(value);
        loadStyles(value, 1);
      }, STYLE_SEARCH_DEBOUNCE_MS),
    [loadStyles],
  );

  useEffect(() => {
    loadStyles("", 1);
    return () => debouncedStyleSearch.cancel();
  }, [loadStyles, debouncedStyleSearch]);

  const handleStylePopupScroll = (event) => {
    const target = event.target;
    const nearBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 24;
    if (nearBottom && !styleLoading && stylePaging.current < stylePaging.last) {
      loadStyles(styleSearch, stylePaging.current + 1);
    }
  };

  const visibleStyleOptions = useMemo(
    () => (selectedStyle ? mergeById([selectedStyle], styleOptions) : styleOptions),
    [selectedStyle, styleOptions],
  );

  const handleStyleChange = (styleId) => {
    const style = visibleStyleOptions.find((s) => Number(s.id) === Number(styleId)) || null;
    setSelectedStyle(style);
    setInventoryItems([]);
    setInventoryError(null);
    pendingItemMatchRef.current = null;
    const suppliers = style?.supplier_ids || [];
    form.setFieldsValue({
      supplier_id: suppliers.length === 1 ? suppliers[0].id : undefined,
      inventory_item_id: undefined,
      color_id: undefined,
    });
  };

  const handleSupplierChange = () => {
    pendingItemMatchRef.current = null;
    form.setFieldsValue({ inventory_item_id: undefined });
  };

  /* -------------------------------- Inventory -------------------------------- */

  const styleId = selectedStyle?.id ?? null;

  useEffect(() => {
    if (!styleId || !supplierId) {
      setInventoryItems([]);
      setInventoryError(null);
      setInventoryLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setInventoryLoading(true);
    setInventoryError(null);
    fetchInventoryDetails({ styleId, supplierId }, controller.signal)
      .then((items) => {
        setInventoryItems(items);
        const pending = pendingItemMatchRef.current;
        if (!pending) return;
        pendingItemMatchRef.current = null;
        const match = items.find(
          (i) =>
            Number(i.item_id) === Number(pending.item_id) &&
            (pending.sub_style_id == null || Number(i.sub_style_id) === Number(pending.sub_style_id)),
        );
        if (match) {
          form.setFieldsValue({ inventory_item_id: match.id });
        }
      })
      .catch((error) => {
        if (!isRequestCancelled(error)) setInventoryError(error?.message || "Failed to load inventory details.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setInventoryLoading(false);
      });
    return () => controller.abort();
  }, [styleId, supplierId, inventoryReloadKey, form]);

  const selectedItem = useMemo(
    () => inventoryItems.find((i) => Number(i.id) === Number(inventoryItemId)) || null,
    [inventoryItems, inventoryItemId],
  );

  const itemOptions = useMemo(
    () =>
      inventoryItems.filter(
        (i) => Number(i.inventory?.rcv_qty) > 0 || Number(i.id) === Number(inventoryItemId),
      ),
    [inventoryItems, inventoryItemId],
  );

  const handleItemChange = (itemId) => {
    const item = inventoryItems.find((i) => Number(i.id) === Number(itemId)) || null;
    const colorId = guessColorId(selectedStyle, item);
    if (colorId) form.setFieldsValue({ color_id: colorId });
  };

  // Edit mode: saved item not in the current inventory response → show saved values, don't block.
  const usingSavedItem =
    mode === "edit" &&
    !!record &&
    !selectedItem &&
    Number(record.style_id) === Number(styleId) &&
    Number(record.supplier_id) === Number(supplierId);

  const receivedDisplay = selectedItem
    ? {
      qty: selectedItem.inventory?.rcv_qty,
      unit: selectedItem.inventory?.unit?.name || "",
      date: selectedItem.inventory?.receive_date,
      roll: selectedItem.inventory?.roll_qty,
    }
    : usingSavedItem
      ? { qty: record.received_qty, unit: "", date: record.received_date, roll: record.received_roll }
      : null;

  /* ------------------------------- Edit hydration ------------------------------ */

  useEffect(() => {
    if (mode !== "edit") return undefined;
    const controller = new AbortController();
    setRecordLoading(true);
    setRecordError(null);

    (async () => {
      try {
        const rec = await fetchInspectionById(editId, controller.signal);
        let style = null;
        if (rec.style?.style_no) {
          try {
            const result = await fetchStyles({ search: rec.style.style_no, page: 1, perPage: STYLE_PAGE_SIZE }, controller.signal);
            style = result.items.find((s) => Number(s.id) === Number(rec.style_id)) || null;
          } catch (error) {
            if (isRequestCancelled(error)) throw error;
          }
        }
        style = style ? withRecordFallbacks(style, rec) : styleFromRecord(rec);

        const defectValues = {};
        getRecordDetails(rec).forEach((d) => {
          const value = Number(d.defect_value);
          if (value > 0) defectValues[String(d.defect_subcategory_id)] = value;
        });

        pendingItemMatchRef.current = { item_id: rec.item_id, sub_style_id: rec.sub_style_id };
        setRecord(rec);
        setSelectedStyle(style);
        form.setFieldsValue({
          style_id: style.id,
          supplier_id: rec.supplier_id,
          color_id: rec.color_id ?? undefined,
          inspection_date: rec.inspection_date ? moment(rec.inspection_date) : undefined,
          inspected_roll: rec.inspected_roll,
          tag_length: rec.tag_length != null ? Number(rec.tag_length) : undefined,
          tag_actual_length: rec.tag_actual_length != null ? Number(rec.tag_actual_length) : undefined,
          tag_width: rec.tag_width != null ? Number(rec.tag_width) : undefined,
          tag_actual_width: rec.tag_actual_width != null ? Number(rec.tag_actual_width) : undefined,
          note: rec.note || "",
          defects: defectValues,
        });
      } catch (error) {
        if (!isRequestCancelled(error)) setRecordError(error?.message || "Failed to load fabric inspection.");
      } finally {
        if (!controller.signal.aborted) setRecordLoading(false);
      }
    })();

    return () => controller.abort();
  }, [mode, editId, recordReloadKey, form]);

  /* ------------------------------ Summary + validity ----------------------------- */

  const summary = summarizeDefects(defects || {}, tagActualLength, tagActualWidth, defectCategories);
  const detailCount = buildInspectionDetails(
    defects || {},
    mode === "edit" ? getRecordDetails(record) : [],
    tagActualLength,
    tagActualWidth,
    mode,
    defectCategories,
  ).length;

  const missingSalesContract = !!selectedStyle && !selectedStyle.active_sales_contract_map_info?.sales_contract_id;
  const missingReceivedDate = !!receivedDisplay && !receivedDisplay.date;

  const requiredFilled =
    !!selectedStyle &&
    !!supplierId &&
    (!!selectedItem || usingSavedItem) &&
    !!inspectionDate &&
    inspectedRoll !== null &&
    inspectedRoll !== undefined &&
    Number(tagActualLength) > 0 &&
    Number(tagActualWidth) > 0;

  const canSubmit =
    requiredFilled &&
    !missingSalesContract &&
    !missingReceivedDate &&
    detailCount > 0 &&
    !inventoryLoading &&
    !recordLoading &&
    !recordError &&
    !saving;

  /* ---------------------------------- Submit ---------------------------------- */

  const applyServerErrors = (fieldErrors) => {
    const byFormField = {};
    const unmapped = [];
    Object.entries(fieldErrors || {}).forEach(([serverField, messages]) => {
      const list = Array.isArray(messages) ? messages : [String(messages)];
      const formField = SERVER_FIELD_TO_FORM_FIELD[serverField];
      if (formField) {
        byFormField[formField] = [...(byFormField[formField] || []), ...list];
      } else {
        unmapped.push(...list);
      }
    });
    form.setFields(Object.entries(byFormField).map(([name, errors]) => ({ name, errors })));
    setGeneralErrors(Array.from(new Set(unmapped)));
  };

  const handleSubmit = async () => {
    if (savingRef.current || !canSubmit) return;
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const values = form.getFieldsValue(true);
    const payload = buildInspectionPayload(
      {
        style: selectedStyle,
        supplierId: values.supplier_id,
        inventoryItem: selectedItem,
        savedReceived: usingSavedItem
          ? { received_qty: record.received_qty, received_date: record.received_date, received_roll: record.received_roll }
          : null,
        savedItem: usingSavedItem
          ? { item_category_id: record.item_category_id, item_id: record.item_id, sub_style_id: record.sub_style_id }
          : null,
        colorId: values.color_id,
        inspectionDate: values.inspection_date,
        inspectedRoll: values.inspected_roll,
        tagLength: values.tag_length,
        tagActualLength: values.tag_actual_length,
        tagWidth: values.tag_width,
        tagActualWidth: values.tag_actual_width,
        note: values.note,
        defectCounts: values.defects || {},
        existingDetails: mode === "edit" ? getRecordDetails(record) : [],
        defectCategories,
      },
      mode,
    );

    savingRef.current = true;
    setSaving(true);
    setGeneralErrors([]);
    const result = mode === "edit" ? await updateInspection(editId, payload) : await createInspection(payload);
    savingRef.current = false;
    setSaving(false);

    if (result.ok) {
      onSaved(result.message);
      return;
    }
    message.error(result.message);
    applyServerErrors(result.fieldErrors);
  };

  const handleValuesChange = (changed) => {
    const names = Object.keys(changed).filter((name) => name !== "defects");
    if (names.length) form.setFields(names.map((name) => ({ name, errors: [] })));
    if (generalErrors.length) setGeneralErrors([]);
  };

  /* ---------------------------------- Render ---------------------------------- */

  const colorOptions = useMemo(() => {
    const seen = new Set();
    return (selectedStyle?.color_map || [])
      .filter((c) => {
        if (!c.color_id || seen.has(Number(c.color_id))) return false;
        seen.add(Number(c.color_id));
        return true;
      })
      .map((c) => ({ value: Number(c.color_id), label: c.color_info?.name || `Color #${c.color_id}` }));
  }, [selectedStyle]);

  const fabricItemEmptyText = inventoryLoading
    ? "Loading..."
    : inventoryItems.length === 0
      ? "No fabric items found for this style and supplier"
      : "Nothing has been received yet for this style and supplier";

  // Edit mode blocks the whole form until the record and its inventory are ready.
  const editPending = mode === "edit" && (recordLoading || inventoryLoading || defectCategoriesLoading);

  return (
    <Modal
      open
      className="fabric-inspection-modal"
      title={
        <Space>
          <FileSearchOutlined style={{ color: "#1890ff" }} />
          <span>{mode === "edit" ? "Edit Fabric Inspection" : "Add Fabric Inspection"}</span>
        </Space>
      }
      width={1150}
      maskClosable={false}
      onCancel={saving ? undefined : onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={saving} disabled={!canSubmit} onClick={handleSubmit}>
          {mode === "edit" ? "Update" : "Save"}
        </Button>,
      ]}
    >
      {recordError ? (
        <Result
          status="error"
          title="Could not load this inspection"
          subTitle={recordError}
          extra={
            <Button icon={<ReloadOutlined />} onClick={() => setRecordReloadKey((k) => k + 1)}>
              Retry
            </Button>
          }
        />
      ) : (
        <Spin spinning={editPending} tip="Loading inspection..." size="large">
          <Form form={form} layout="vertical" size="small" onValuesChange={handleValuesChange} requiredMark>
            <div className="fi-section-title">Style & Supplier</div>
            <Row gutter={12}>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Style"
                  name="style_id"
                  rules={[{ required: true, message: "Style is required" }]}
                  extra={
                    missingSalesContract ? (
                      <Text type="danger">This style has no active sales contract; it can't be saved.</Text>
                    ) : null
                  }
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Search style no."
                    filterOption={false}
                    onSearch={debouncedStyleSearch}
                    onChange={handleStyleChange}
                    onPopupScroll={handleStylePopupScroll}
                    loading={styleLoading}
                    notFoundContent={styleLoading ? <Spin size="small" /> : "No styles found"}
                    options={visibleStyleOptions.map((s) => ({ value: Number(s.id), label: s.style_no }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Buyer">
                  <Input disabled value={selectedStyle?.buyer_info?.name || ""} placeholder="Auto-filled from style" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Supplier Name" name="supplier_id" rules={[{ required: true, message: "Supplier is required" }]}>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder={selectedStyle ? "Select supplier" : "Select a style first"}
                    disabled={!selectedStyle}
                    onChange={handleSupplierChange}
                    options={(selectedStyle?.supplier_ids || []).map((s) => ({ value: Number(s.id), label: s.name }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div className="fi-section-title">Fabric Item (from inventory)</div>
            {inventoryError && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 8 }}
                message={inventoryError}
                action={
                  <Button size="small" onClick={() => setInventoryReloadKey((k) => k + 1)}>
                    Retry
                  </Button>
                }
              />
            )}
            {usingSavedItem && !inventoryLoading && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 8 }}
                message="The saved fabric item is no longer pending in inventory. Its saved received values are shown and will be kept."
              />
            )}
            <Spin spinning={mode === "create" && inventoryLoading} tip="Loading fabric items...">
              <Row gutter={12}>
                <Col xs={24} md={10}>
                  <Form.Item
                    label="Fabric Item"
                    name="inventory_item_id"
                    rules={[{ required: !usingSavedItem, message: "Fabric item is required" }]}
                    extra={
                      missingReceivedDate ? (
                        <Text type="danger">This item has no received date yet; it can't be saved.</Text>
                      ) : null
                    }
                  >
                    <Select
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      placeholder={styleId && supplierId ? "Select fabric item" : "Select style and supplier first"}
                      disabled={!styleId || !supplierId}
                      loading={inventoryLoading}
                      onChange={handleItemChange}
                      notFoundContent={fabricItemEmptyText}
                      options={itemOptions.map((i) => {
                        const received = Number(i.inventory?.rcv_qty) > 0;
                        const name = i.inventory?.item?.name || `Item #${i.item_id}`;
                        return {
                          value: Number(i.id),
                          label: received ? name : `${name} (not received)`,
                        };
                      })}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={5}>
                  <Form.Item label="Received Quantity">
                    <Input
                      disabled
                      value={receivedDisplay ? `${receivedDisplay.qty ?? ""} ${receivedDisplay.unit}`.trim() : ""}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={5}>
                  <Form.Item label="Received Date">
                    <Input disabled value={receivedDisplay ? formatDate(receivedDisplay.date) : ""} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item label="Total Roll">
                    <Input disabled value={receivedDisplay?.roll ?? ""} />
                  </Form.Item>
                </Col>
              </Row>
            </Spin>

            <div className="fi-section-title">Inspection</div>
            <Row gutter={12}>
              <Col xs={12} md={6}>
                <Form.Item
                  label="Inspection Date"
                  name="inspection_date"
                  rules={[{ required: true, message: "Inspection date is required" }]}
                >
                  <DatePicker style={{ width: "100%" }} format={DATE_FORMAT} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item label="Inspected Fabric Color" name="color_id">
                  <Select
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    placeholder={selectedStyle ? "Select color" : "Select a style first"}
                    disabled={!selectedStyle}
                    options={colorOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item
                  label="Inspected Roll No."
                  name="inspected_roll"
                  rules={[{ required: true, message: "Inspected roll no. is required" }]}
                >
                  <InputNumber style={{ width: "100%" }} min={0} precision={0} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col xs={12} md={6}>
                <Form.Item label="TAG - Length" name="tag_length" rules={[{ required: true, message: "TAG - Length is required" }]}>
                  <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item
                  label="ACTUAL - Length"
                  name="tag_actual_length"
                  rules={[{ required: true, message: "Actual length is required" }, positiveNumberRule("Actual length")]}
                >
                  <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item label="TAG - Width" name="tag_width" rules={[{ required: true, message: "TAG - Width is required" }]}>
                  <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item
                  label="ACTUAL - Width"
                  name="tag_actual_width"
                  rules={[{ required: true, message: "Actual width is required" }, positiveNumberRule("Actual width")]}
                >
                  <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Note" name="note">
              <TextArea rows={2} placeholder="Optional" />
            </Form.Item>

            <Divider orientation="left" style={{ margin: "8px 0" }}>
              Defects
            </Divider>
            <DefectInputs categories={defectCategories} loading={defectCategoriesLoading} />

            {generalErrors.length > 0 && (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: 12 }}
                message="Please fix the following"
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {generalErrors.map((err) => (
                      <li key={err}>{err}</li>
                    ))}
                  </ul>
                }
              />
            )}

            <Row className="fi-summary" gutter={24} align="middle">
              <Col>
                <Text strong>Total Defect:</Text> {summary.totalDefect}
              </Col>
              <Col>
                <Text strong>Penalty Point:</Text> {summary.penaltyPoint}
              </Col>
              <Col>
                <Text strong>Result:</Text>{" "}
                <Text type={summary.status === "Fail" ? "danger" : "success"} strong>
                  {summary.status}
                </Text>
              </Col>
              {detailCount === 0 && (
                <Col>
                  <Text type="warning">Enter at least one defect count; the backend requires one.</Text>
                </Col>
              )}
            </Row>
          </Form>
        </Spin>
      )}
    </Modal>
  );
};

export default FabricInspectionModal;
